import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../../../../context';
import { getErrorMessage } from '@shared/lib/error';
import { useBackToOrder } from './useBackToOrder';
import { closeModal, setModal } from '../model';
import { ExchangeRefundModalData } from '@entities/order';

const errorsRedirectToOrder = [
	'Refund operations with used segments are not permitted',
	'THIS IS A MANUAL OPERATION - SET PENALTY MANUALLY',
	'There is already an open request for a refund'
];

export const useShowRefundErrorModal = () => {
	const dispatch = useDispatch();

	const {
		Engine: { checkInURL, checkInEnabled }
	} = useConfig();

	const { t } = useTranslation('Refund');

	const { onBackToOrder } = useBackToOrder(true);

	const showErrorModal = useCallback(
		(error: unknown) => {
			if (!error) {
				return;
			}

			const message = getErrorMessage(error, 'ErrorModal.DefaultTitle');

			let title = t('ErrorModal.DefaultTitle');
			let description = t('ErrorModal.DefaultDescription');

			if (message.includes('Refund operations with checked segments are not permitted')) {
				title = t('It is not possible to refund a ticket that has been checked-in for a flight');
				description = `${t('If you wish to make a refund, please cancel your flight check-in.')} ${
					checkInEnabled
						? t(
								`You can do it yourself by going to <a href='{{link}}' target='_blank'>Online Registration section</a>.`,
								{ link: checkInURL }
						  )
						: ''
				}`;
			} else if (message.includes('Refund operations with used segments are not permitted')) {
				title = t('It is not possible to refund a used ticket');
				description = t('The ticket has been flown on, you cannot return a used ticket.');
			} else if (message.includes('Refund operation allowed only in manual mode')) {
				title = t('To make an exchange please contact a customer support service');
				description = '';
			} else if (message.includes('There is already an open request for a refund')) {
				title = t('There is already an open request for a refund');
				description = '';
			} else if (message.includes('This operation is not available')) {
				title = t('This operation is not available');
			}

			const isBackToOrder = errorsRedirectToOrder.includes(message);

			const modalData: ExchangeRefundModalData = {
				status: 'error',
				title,
				description,
				success: {
					text: isBackToOrder ? t('ErrorModal.BackToOrder') : t('ErrorModal.BackToRequest'),
					onClick: () => {
						dispatch(closeModal());

						isBackToOrder && onBackToOrder();
					}
				}
			};

			dispatch(setModal(modalData));
		},
		[checkInURL, checkInEnabled, onBackToOrder, dispatch, t]
	);

	return showErrorModal;
};
